import { useState } from "react";
import { Button, ButtonVariant, CheckboxCard } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Briefcase02Icon,
  BankIcon,
  PieChart01Icon,
  ArrowRight02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import { VerifyMethodType } from "shared/types";
import { CombinedIncomeSubStep } from "../../../constants";
import { DocumentType } from "../types";
import { CombinedIncomeLayout } from "../components/Layout";
import { useCombinedIncome } from "../context";
import SkipDocumentVerificationModal from "../components/SkipDocumentVerificationModal";
import { getErrorMessage } from "utils/error";

const documentOptions = [
  {
    type: DocumentType.Paystub,
    label: "Pay stubs",
    icon: Briefcase02Icon,
    verifyMethod: VerifyMethodType.Paystub
  },
  {
    type: DocumentType.BankStatement,
    label: "Bank statements",
    icon: BankIcon,
    verifyMethod: VerifyMethodType.BankStatement
  },
  {
    type: DocumentType.W2,
    label: "W2",
    icon: PieChart01Icon,
    verifyMethod: VerifyMethodType.W2
  }
];

export const SelectDocumentTypeStep = () => {
  const { updateStepMutation, refetchVerification } = useCombinedIncome();

  const [isProcessing, setIsProcessing] = useState(false);
  const [
    skipDocumentVerificationModalOpen,
    setSkipDocumentVerificationModalOpen
  ] = useState(false);

  const handleSelect = async (documentType: DocumentType) => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      await updateStepMutation.mutateAsync({
        lastStep: CombinedIncomeSubStep.DocumentUpload,
        currentDocumentType: documentType
      });

      refetchVerification();
    } catch (error) {
      toast.error(
        getErrorMessage(error) || "Failed to process document selection"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <CombinedIncomeLayout
      title="How would you like to verify applicant's income history?"
      footer={
        <Button
          variant={ButtonVariant.BASE}
          className="w-[282px]"
          iconPosition="right"
          HugeIcon={ArrowRight02Icon}
          onClick={() => setSkipDocumentVerificationModalOpen(true)}
        >
          Skip document verification
        </Button>
      }
    >
      <div className="mt-4 space-y-3">
        {documentOptions.map((option) => (
          <CheckboxCard
            key={option.type}
            checked={false}
            id={option.type}
            label={
              <div className="flex items-center gap-4">
                <div className="flex justify-center items-center bg-primary-20 w-10 h-10 rounded-lg">
                  <HugeiconsIcon
                    icon={option.icon}
                    size={24}
                    className="text-primary-500"
                  />
                </div>
                <p className="text-base text-alpha-900 font-bold">
                  {option.label}
                </p>
              </div>
            }
            name={option.type}
            value={option.type}
            wrapperClassName="w-full"
            onChange={() => handleSelect(option.type)}
          />
        ))}
      </div>
      <SkipDocumentVerificationModal
        opened={skipDocumentVerificationModalOpen}
        onClose={() => setSkipDocumentVerificationModalOpen(false)}
      />
    </CombinedIncomeLayout>
  );
};
